import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { useMe } from "../../lib/me";
import { Button, cx, ErrorText } from "../../ui";
import { Icon, IconButton, type IconName } from "../../ui/icons";
import { Dialog } from "../../ui/overlay";
import { useToast } from "../../ui/toast";
import type { Task } from "./model";
import { TaskProperties } from "./TaskProperties";
import { RoundCheck } from "./TaskRow";
import { useAutosave, useSyncedText, type SaveState } from "./use-autosave";
import { memberLabel, useMembers, useTaskActions } from "./use-task-actions";
import { useTask } from "./use-tasks";

/** How long typing pauses before a title or notes change is saved; leaving the field saves at once. */
const TYPING_PAUSE_MS = 800;

function isTyping(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName));
}

/** One task as its own page (WP-053): edited in place, saved as it changes. */
export function TaskPage({ workspaceId, taskId }: { workspaceId: string; taskId: string }) {
  const state = useTask(workspaceId, taskId);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || isTyping(e.target)) return;
      void navigate({ to: "/w/$workspaceId/tasks", params: { workspaceId } });
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navigate, workspaceId]);

  if (state.status === "ready") return <TaskEditor workspaceId={workspaceId} task={state.task} />;
  return (
    <div className="flex flex-col gap-4">
      <Header workspaceId={workspaceId} title={state.status === "loading" ? "" : "Task"} />
      {state.status === "loading" ? <p className="text-sm text-muted">Loading the task…</p> : null}
      {state.status === "error" ? <ErrorText>The task could not be loaded: {state.message}</ErrorText> : null}
      {state.status === "missing" ? (
        <p className="text-sm text-muted">
          This task does not exist, or it is private and not yours.{" "}
          <Link to="/w/$workspaceId/tasks" params={{ workspaceId }} className="text-accent underline">
            Back to tasks
          </Link>
        </p>
      ) : null}
    </div>
  );
}

function Header({ workspaceId, title, children }: { workspaceId: string; title: string; children?: ReactNode }) {
  return (
    <div className="sticky top-12 z-10 flex h-11 items-center gap-1 border-b border-border bg-bg md:top-0">
      <Link
        to="/w/$workspaceId/tasks"
        params={{ workspaceId }}
        aria-label="Back to tasks"
        title="Back to tasks (Esc)"
        className="inline-grid size-8 shrink-0 place-items-center rounded-md text-muted hover:bg-surface-2 hover:text-text"
      >
        <Icon name="back" />
      </Link>
      <p className="min-w-0 flex-1 truncate px-1 text-sm text-muted" aria-hidden="true">
        {title}
      </p>
      {children}
    </div>
  );
}

function TaskEditor({ workspaceId, task }: { workspaceId: string; task: Task }) {
  const me = useMe();
  const ws = me.workspaces.find((w) => w.id === workspaceId);
  const personal = ws?.name === "Personal";
  const canDelete = task.creatorUid === me.uid || ws?.role === "owner";
  const members = useMembers(workspaceId);
  const actions = useTaskActions();
  const autosave = useAutosave(workspaceId, task.id);
  const navigate = useNavigate();
  const titleId = useId();

  const [title, setTitle] = useSyncedText(task.title);
  const [notes, setNotes] = useSyncedText(task.notes);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The latest unsaved text, so a pause, a blur or leaving the page each save the same thing once.
  const pending = useRef<{ title?: string; notes?: string }>({});
  const stored = useRef(task);
  stored.current = task;

  const flush = useRef(() => {});
  flush.current = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    const patch: { title?: string; notes?: string } = {};
    const next = pending.current;
    pending.current = {};
    if (next.title !== undefined && next.title.trim() !== "" && next.title.trim() !== stored.current.title) patch.title = next.title.trim();
    if (next.notes !== undefined && next.notes !== stored.current.notes) patch.notes = next.notes;
    autosave.save(patch);
  };
  useEffect(() => () => flush.current(), []);

  const edit = (field: "title" | "notes", value: string) => {
    (field === "title" ? setTitle : setNotes)(value);
    pending.current = { ...pending.current, [field]: value };
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = setTimeout(() => flush.current(), TYPING_PAUSE_MS);
  };

  const done = task.status === "done";
  const emptyTitle = title.trim() === "";

  return (
    <div className="flex flex-col gap-6 pb-16">
      <Header workspaceId={workspaceId} title={task.title}>
        <SaveIndicator state={autosave.state} onRetry={autosave.retry} />
        {task.status === "cancelled" ? null : (
          <IconButton
            icon={done ? "reopen" : "task"}
            tone={done ? "active" : "plain"}
            label={done ? "Reopen" : "Mark done"}
            className="size-8"
            onClick={() => actions.toggle(workspaceId, task.id, !done)}
          />
        )}
        <MoreMenu
          task={task}
          canDelete={canDelete}
          onCancel={() => actions.setStatus(workspaceId, task.id, task.status === "cancelled" ? "open" : "cancelled")}
          onDelete={() => setConfirmDelete(true)}
        />
      </Header>

      <article aria-labelledby={titleId} className="mx-auto flex w-full max-w-[720px] flex-col gap-6">
        <div className="flex items-start gap-3">
          <RoundCheck
            size="lg"
            checked={done}
            disabled={task.status === "cancelled"}
            label={done ? "Reopen" : "Mark done"}
            onChange={() => actions.toggle(workspaceId, task.id, !done)}
          />
          <div className="min-w-0 flex-1">
            <h1 id={titleId} className="sr-only">
              {task.title}
            </h1>
            <textarea
              aria-label="Title"
              rows={1}
              maxLength={300}
              value={title}
              onChange={(e) => edit("title", e.target.value.replace(/\n/g, " "))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              onBlur={() => {
                flush.current();
                if (emptyTitle) setTitle(stored.current.title);
              }}
              aria-invalid={emptyTitle}
              className={cx(
                "field-sizing-content w-full resize-none rounded-md border-0 bg-transparent px-1 py-0.5 text-2xl font-semibold text-text outline-none",
                "hover:bg-surface-2 focus:bg-surface-2",
                task.status === "open" ? "" : "text-muted line-through",
              )}
            />
            {emptyTitle ? <p className="px-1 text-xs text-danger">A task needs a title. Leave the field to keep the old one.</p> : null}
          </div>
        </div>

        <TaskProperties workspaceId={workspaceId} task={task} personal={personal} myUid={me.uid} onSave={autosave.save} />

        <div className="flex flex-col gap-1 border-t border-border pt-4">
          <textarea
            aria-label="Notes"
            placeholder="Add notes…"
            maxLength={10_000}
            value={notes}
            onChange={(e) => edit("notes", e.target.value)}
            onBlur={() => flush.current()}
            className="field-sizing-content min-h-[40vh] w-full resize-none border-0 bg-transparent px-1 text-sm leading-6 text-text outline-none placeholder:text-muted"
          />
        </div>

        <p className="px-1 text-xs text-muted">
          Created by {task.creatorUid === me.uid ? "you" : memberLabel(members.data, task.creatorUid)} ·{" "}
          {task.createdAt.toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
        </p>
      </article>

      {confirmDelete ? (
        <Dialog title="Delete this task?" description={`“${task.title}” will be deleted. You can undo it from Settings → Activity.`} onClose={() => setConfirmDelete(false)}>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setConfirmDelete(false)}>Keep it</Button>
            <Button
              variant="danger"
              disabled={actions.removing}
              onClick={() =>
                void actions.remove(workspaceId, task.id, () => {
                  setConfirmDelete(false);
                  void navigate({ to: "/w/$workspaceId/tasks", params: { workspaceId } });
                })
              }
            >
              Delete
            </Button>
          </div>
        </Dialog>
      ) : null}
    </div>
  );
}

function SaveIndicator({ state, onRetry }: { state: SaveState; onRetry: () => void }) {
  return (
    <span role="status" className="flex items-center gap-1 px-2 text-xs text-muted">
      {state.status === "saving" ? "Saving…" : null}
      {state.status === "saved" ? "Saved" : null}
      {state.status === "error" ? (
        <>
          <span className="text-danger" title={state.message}>
            Not saved
          </span>
          <button type="button" onClick={onRetry} className="rounded px-1 text-accent hover:underline">
            Retry
          </button>
        </>
      ) : null}
    </span>
  );
}

function MoreMenu({ task, canDelete, onCancel, onDelete }: { task: Task; canDelete: boolean; onCancel: () => void; onDelete: () => void }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<CSSProperties>({});
  const menuId = useId();
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e: PointerEvent) => {
      if (root.current !== null && !root.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  const items: Array<{ key: string; label: string; icon: IconName; danger?: boolean; run: () => void }> = [
    {
      key: "copy",
      label: "Copy link",
      icon: "link",
      run: () => {
        navigator.clipboard.writeText(window.location.href).then(
          () => toast({ message: "Link copied." }),
          () => toast({ tone: "error", message: "The link could not be copied." }),
        );
      },
    },
    { key: "cancel", label: task.status === "cancelled" ? "Reopen task" : "Cancel task", icon: task.status === "cancelled" ? "reopen" : "cancel", run: onCancel },
    ...(canDelete ? [{ key: "delete", label: "Delete…", icon: "trash" as const, danger: true, run: onDelete }] : []),
  ];

  return (
    <div
      ref={root}
      className="relative"
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) {
          e.preventDefault();
          e.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <IconButton
        icon="more"
        label="More actions"
        className="size-8"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setPlace({ right: Math.max(8, window.innerWidth - r.right), top: r.bottom + 4 });
          setOpen((o) => !o);
        }}
      />
      {open ? (
        <div id={menuId} role="menu" aria-label="More actions" style={place} className="fixed z-30 w-52 rounded-lg border border-border bg-surface py-1 shadow-lg">
          {items.map((m) => (
            <button
              key={m.key}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                m.run();
              }}
              className={cx(
                "flex min-h-9 w-full items-center gap-3 px-3 text-left text-sm hover:bg-surface-2",
                m.danger === true ? "border-t border-border text-danger" : "text-text",
              )}
            >
              <Icon name={m.icon} className={m.danger === true ? "" : "text-muted"} />
              {m.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
